/**
 * MobileStationSettings.tsx
 *
 * Mobile view for enabling/disabling stations.
 * Simple toggle switches for each station.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useLanguage } from '../../../i18n/LanguageContext';
import stationManagementService, { StationWithDetails } from '../../../services/stationManagementService';
import toast from 'react-hot-toast';
import {
  LayoutGrid,
  Loader2,
  Settings,
  MapPin,
  Power,
  Filter,
  CheckCircle,
} from 'lucide-react';

const MobileStationSettings: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [showFilter, setShowFilter] = useState(false);

  // Fetch stations
  const { data: stations, isLoading } = useQuery(
    ['mobile-station-settings'],
    () => stationManagementService.getStations(),
    { staleTime: 30000 }
  );

  // Get unique locations
  const locations = React.useMemo(() => {
    if (!stations) return [];
    const uniqueLocations = new Set(
      stations.map((s: StationWithDetails) => s.location_name || s.location_code).filter(Boolean)
    );
    return Array.from(uniqueLocations).sort() as string[];
  }, [stations]);

  // Filter stations by location
  const filteredStations = React.useMemo(() => {
    if (!stations) return [];
    if (!selectedLocation) return stations;
    return stations.filter(
      (s: StationWithDetails) =>
        (s.location_name || s.location_code) === selectedLocation
    );
  }, [stations, selectedLocation]);

  // Toggle station mutation
  const toggleMutation = useMutation(
    ({ stationId, enabled }: { stationId: number; enabled: boolean }) =>
      stationManagementService.toggleStationEnabled(stationId, enabled),
    {
      onSuccess: (_, { enabled }) => {
        toast.success(enabled ? (t('supervisor.stationEnabled') || 'Station enabled') : (t('supervisor.stationDisabled') || 'Station disabled'));
        queryClient.invalidateQueries(['mobile-station-settings']);
        queryClient.invalidateQueries(['mobile-supervisor-stations']);
      },
      onError: () => {
        toast.error(t('common.error') || 'Failed to update station');
      },
    }
  );

  const handleToggle = (station: StationWithDetails) => {
    const newEnabled = station.enabled === false;
    toggleMutation.mutate({ stationId: station.station_id, enabled: newEnabled });
  };

  // Count enabled/disabled
  const enabledCount = filteredStations.filter((s: StationWithDetails) => s.enabled !== false).length;
  const disabledCount = filteredStations.filter((s: StationWithDetails) => s.enabled === false).length;

  return (
    <div className="min-h-screen flex flex-col bg-gray-100 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-blue-600 dark:bg-blue-700 text-white px-4 py-3 sticky top-0 z-10 flex items-center gap-3">
        <button
          onClick={() => navigate('/supervisor')}
          className="p-2 -ml-2 rounded-lg hover:bg-white/10 active:bg-white/20 transition-colors"
        >
          <LayoutGrid className="w-6 h-6" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold">{t('mobileSupervisor.stationManagement') || 'Station Settings'}</h1>
          <p className="text-sm opacity-80">
            {enabledCount} {t('supervisor.enabled') || 'enabled'} • {disabledCount} {t('supervisor.disabled') || 'disabled'}
          </p>
        </div>
        <button
          onClick={() => setShowFilter(!showFilter)}
          className={`p-2 rounded-lg transition-colors ${showFilter ? 'bg-white/20' : 'hover:bg-white/10'}`}
        >
          <Filter className="w-5 h-5" />
        </button>
        {isLoading && <Loader2 className="w-5 h-5 animate-spin opacity-70" />}
      </header>

      {/* Filter Bar */}
      {showFilter && (
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
          <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">
            {t('supervisor.filterByLocation') || 'Filter by Location'}
          </label>
          <select
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">{t('supervisor.allLocations') || 'All Locations'}</option>
            {locations.map((loc) => (
              <option key={loc} value={loc}>{loc}</option>
            ))}
          </select>
        </div>
      )}

      {/* Station List */}
      <main className="flex-1 p-4 space-y-3">
        {isLoading && !stations ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : filteredStations.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            {t('supervisor.noStationsFound') || 'No stations found'}
          </div>
        ) : (
          filteredStations.map((station: StationWithDetails) => {
            const isEnabled = station.enabled !== false;
            const isToggling = toggleMutation.isLoading && toggleMutation.variables?.stationId === station.station_id;
            const hasActiveSession = station.session_status === 'active' || station.session_status === 'in_progress';

            return (
              <div
                key={station.station_id}
                className={`bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 ${
                  !isEnabled ? 'opacity-60' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                        {station.station_name}
                      </h3>
                      {hasActiveSession && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                          {t('supervisor.active') || 'Active'}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                      <MapPin className="w-3.5 h-3.5" />
                      <span>{station.location_name || station.location_code || '-'}</span>
                    </div>
                  </div>

                  {/* Toggle Switch */}
                  <button
                    onClick={() => handleToggle(station)}
                    disabled={isToggling || hasActiveSession}
                    className={`relative w-14 h-8 rounded-full transition-colors ${
                      isEnabled
                        ? 'bg-green-500'
                        : 'bg-gray-300 dark:bg-gray-600'
                    } ${hasActiveSession ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {isToggling ? (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Loader2 className="w-5 h-5 animate-spin text-white" />
                      </div>
                    ) : (
                      <div
                        className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${
                          isEnabled ? 'translate-x-7' : 'translate-x-1'
                        }`}
                      >
                        {isEnabled && (
                          <CheckCircle className="w-4 h-4 text-green-500 absolute top-1 left-1" />
                        )}
                      </div>
                    )}
                  </button>
                </div>

                {/* Warning if has active session */}
                {hasActiveSession && (
                  <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                    {t('supervisor.cannotDisableActiveStation') || 'Cannot disable while session is active'}
                  </p>
                )}
              </div>
            );
          })
        )}
      </main>
    </div>
  );
};

export default MobileStationSettings;
